import React, { useContext } from "react";
import { Link, NavLink } from "react-router-dom";
import styled from "styled-components";
import { Alignment, AnchorButton, Button, Navbar } from "@blueprintjs/core";
import {
  faBook,
  faCog,
  faDatabase,
  faPencilAlt,
  faSignInAlt,
  faSignOut,
  faSortDown,
  faTags,
  faUser,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu, Tooltip } from "@mantine/core";

import { useAuth } from "@api";
import { useBasePath } from "@hooks";
import { useIsDesktop } from "@hooks/useIsDesktop";
import { OrgContext, UserContext } from "@providers";
import { Feature } from "@utils/feature";

import { slugifyOrgName } from "@pages/dashboard/utils";

import { BrandIcon, NavbarLogo } from "./NavbarLogo";
import { CommunityTierTag, TeamTierTag } from "./TierTag";
import { UserPhoto } from "./UserPhoto";

const SignInLink = styled.a`
  color: var(--global-dark-gray);
`;

interface Props {
  minimal?: boolean;
  showLogin?: boolean;
}

const TitleLink: React.FC<React.PropsWithChildren<{ to: string }>> = (
  props
) => (
  <NavLink
    role="button"
    className="bp3-button bp3-minimal"
    activeClassName="bp3-intent-primary"
    {...props}
  />
);

export const AppNavbar: React.FC<Props> = ({
  minimal = false,
  showLogin = true,
}) => {
  const { user } = useContext(UserContext);
  const { org, setOrg } = useContext(OrgContext);
  const { install, logout } = useAuth();
  const isDesktop = useIsDesktop();
  const basePath = useBasePath();
  const registryPages = ["/explore", "/r"];
  // TODO update when we get rid of the old playground
  // we will no longer need "/s" because snippets will be at "/playground/s"
  const editorPages = ["/playground", "/s"];

  const orgLandingPage = org?.name
    ? `/orgs/${slugifyOrgName(org?.name || "-")}/`
    : null;

  // If we are logged in, the app landing page will be dashboard.
  // If not, we will go to the new app landing page information
  const appNavBarPage = orgLandingPage || `/products/semgrep-app`;

  return (
    <div className={minimal ? "minimal" : "normal"}>
      {/* height: "auto" will expand the navbar vertically when its contents wrap on mobile  */}
      <Navbar style={{ height: "auto" }} fixedToTop={!minimal}>
        <Navbar.Group align={Alignment.LEFT}>
          {!minimal && (
            <AnchorButton href={orgLandingPage || `/`} minimal>
              <span className="bp3-button-text">
                {isDesktop ? <NavbarLogo /> : <BrandIcon />}
              </span>
            </AnchorButton>
          )}
          <NavLink
            role="button"
            className="bp3-button bp3-minimal"
            activeClassName="bp3-intent-primary"
            to="/r"
            isActive={() => {
              return (
                registryPages.indexOf(window.location.pathname) > -1 ||
                window.location.pathname.includes("/p/")
              );
            }}
          >
            <span className="bp3-button-text">
              {isDesktop ? "Registry" : <FontAwesomeIcon icon={faDatabase} />}
            </span>
          </NavLink>
          <NavLink
            role="button"
            className="bp3-button bp3-minimal"
            activeClassName="bp3-intent-primary"
            to="/playground"
            isActive={() => {
              return (
                editorPages.indexOf(window.location.pathname) > -1 ||
                window.location.pathname.includes("/s/")
              );
            }}
          >
            <span className="bp3-button-text">
              {isDesktop ? (
                "Playground"
              ) : (
                <FontAwesomeIcon icon={faPencilAlt} />
              )}
            </span>
          </NavLink>
          <Navbar.Heading>
            <AnchorButton
              href={appNavBarPage}
              minimal
              className={
                window.location.pathname === appNavBarPage
                  ? "bp3-intent-primary"
                  : "bp3-button bp3-minimal"
              }
            >
              <span className="bp3-button-text">
                {isDesktop ? "App" : <FontAwesomeIcon icon={faCog} />}
              </span>
            </AnchorButton>
          </Navbar.Heading>
          <Navbar.Heading>
            <AnchorButton
              href="/pricing"
              minimal
              className={
                window.location.pathname === "/pricing"
                  ? "bp3-intent-primary"
                  : "bp3-button bp3-minimal"
              }
            >
              <span className="bp3-button-text">
                {isDesktop ? "Pricing" : <FontAwesomeIcon icon={faTags} />}
              </span>
            </AnchorButton>
          </Navbar.Heading>
          <Menu trigger="hover" closeDelay={200} openDelay={200}>
            <Menu.Target>
              <div>
                <Navbar.Heading>
                  <AnchorButton href="/docs/" minimal>
                    <span className="bp3-button-text">
                      {isDesktop ? "Docs" : <FontAwesomeIcon icon={faBook} />}
                    </span>
                  </AnchorButton>
                </Navbar.Heading>
              </div>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item component="a" href="/docs/">
                {isDesktop ? "Docs" : <FontAwesomeIcon icon={faBook} />}
              </Menu.Item>
              {isDesktop && (
                <Menu.Item component={Link} to={"/learn/"}>
                  Tutorial
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
          {user?.roles?.admin === true ? (
            <TitleLink to={"/admin"}>
              <span className="bp3-button-text">Admin</span>
            </TitleLink>
          ) : null}
        </Navbar.Group>
        <Navbar.Group align={Alignment.RIGHT}>
          {user !== undefined ? (
            <>
              {org?.name && isDesktop ? (
                Feature.hasTeamsTier({ user, org }) ? (
                  <TeamTierTag orgName={org.name} />
                ) : (
                  <CommunityTierTag orgName={org.name} />
                )
              ) : null}
              <Menu shadow="md" width="max-content">
                <Menu.Target>
                  <div>
                    <Navbar.Heading>
                      <Button style={{ fontSize: "16px" }} minimal>
                        {org?.name ?? "(add organization)"}{" "}
                        <FontAwesomeIcon
                          icon={faSortDown}
                          color="var(--global-mid-gray)"
                          pull="right"
                          size="sm"
                        />
                      </Button>
                    </Navbar.Heading>
                  </div>
                </Menu.Target>
                <Menu.Dropdown>
                  {user.orgs.map((availableOrg) => {
                    if (availableOrg.id === org?.id) return null;
                    return (
                      <Menu.Item
                        p="8px"
                        key={availableOrg.id}
                        onClick={() => {
                          setOrg(availableOrg);
                        }}
                      >
                        Switch to <strong>{availableOrg.name}</strong>
                      </Menu.Item>
                    );
                  })}
                  <Menu.Item
                    onClick={async () => {
                      await install(user.auth_provider_type);
                    }}
                  >
                    Add organization
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
              <Navbar.Divider />
              <Menu position="bottom-end">
                <Menu.Target>
                  <div>
                    <Navbar.Heading>
                      <UserPhoto profile_photo={user.profile_photo} />
                    </Navbar.Heading>
                  </div>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    icon={<FontAwesomeIcon icon={faSignOut} />}
                    onClick={async () => {
                      await logout();
                    }}
                  >
                    sign out
                  </Menu.Item>
                  <Menu.Item
                    component={Link}
                    icon={<FontAwesomeIcon icon={faUser} />}
                    to={`${basePath}/settings/account`}
                  >
                    account
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </>
          ) : showLogin ? (
            <Tooltip
              label={
                "Sign in to save custom rules and manage Semgrep policies in CI!"
              }
              position="bottom"
              multiline
              width={240}
              withArrow
              withinPortal
            >
              <SignInLink
                href="/login"
                style={{ color: "--global-purple-highlight" }}
              >
                {isDesktop ? (
                  "Sign in / Sign up free"
                ) : (
                  <FontAwesomeIcon icon={faSignInAlt} />
                )}
              </SignInLink>
            </Tooltip>
          ) : undefined}
        </Navbar.Group>
      </Navbar>
    </div>
  );
};
